import React, { useEffect, useState } from "react";
import { http } from "../lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";

const TABS = [
  { key: "quality", label: "Application Quality" },
  { key: "trends", label: "Hiring Trends" },
  { key: "positions", label: "Position Analytics" },
];

const BAR_COLORS = ["#002FA7", "#3151C6", "#5B7AE0", "#8FA3EC", "#BFCAF3"];

export default function Insights() {
  const [tab, setTab] = useState("quality");
  const [summary, setSummary] = useState(null);
  const [quality, setQuality] = useState([]);
  const [trends, setTrends] = useState([]);
  const [positions, setPositions] = useState([]);

  useEffect(() => {
    Promise.all([
      http.get("/insights/summary"),
      http.get("/insights/application-quality"),
      http.get("/insights/hiring-trends"),
      http.get("/insights/position-analytics"),
    ]).then(([s, q, t, p]) => {
      setSummary(s.data);
      setQuality(q.data);
      setTrends(t.data);
      setPositions(p.data);
    });
  }, []);

  return (
    <div className="space-y-5" data-testid="insights-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Analytics</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-1">Recruitment Insights</h1>
          <p className="text-slate-500 mt-1 text-sm">Operational recruitment metrics.</p>
        </div>
      </div>

      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total Candidates" value={summary.totalCandidates} />
          <Kpi label="Avg Match Score" value={`${summary.averageMatchScore}/100`} />
          <Kpi label="Open Requisitions" value={summary.openRequisitions} />
          <Kpi label="In Interview" value={summary.byStatus?.Interview || 0} />
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="tabs-bar px-4">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`insights-tab-${t.key}`}
              className={`tab-btn ${tab === t.key ? "active" : ""}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="p-6">
          {tab === "quality" && <ApplicationQuality data={quality} />}
          {tab === "trends" && <HiringTrends data={trends} />}
          {tab === "positions" && <PositionAnalytics data={positions} />}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="card p-4">
      <div className="label-eyebrow !text-[10px] mb-1.5">{label}</div>
      <div className="font-display text-2xl font-bold">{value}</div>
    </div>
  );
}

function ApplicationQuality({ data }) {
  return (
    <div>
      <h3 className="font-display text-lg font-bold mb-3">Application Quality (Match Score buckets)</h3>
      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 16, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
            <XAxis dataKey="range" tick={{ fontSize: 12, fill: "#64748B" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
            <Tooltip />
            <Bar dataKey="count" radius={[2, 2, 0, 0]}>
              {data.map((_d, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function HiringTrends({ data }) {
  return (
    <div>
      <h3 className="font-display text-lg font-bold mb-1">Hiring Trends</h3>
      <div className="text-sm text-slate-500 mb-3">Application Trends (Last 90 Days)</div>
      <div style={{ width: "100%", height: 360 }}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: "#64748B" }}
              interval={Math.floor(data.length / 6)}
            />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} label={{ value: "Applications", angle: -90, position: "insideLeft", fontSize: 12, fill: "#64748B" }} />
            <Tooltip />
            <Line type="monotone" dataKey="applications" stroke="#002FA7" strokeWidth={2} dot={{ r: 2 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function PositionAnalytics({ data }) {
  return (
    <div>
      <h3 className="font-display text-lg font-bold mb-3">Position Analytics</h3>
      <div style={{ width: "100%", height: 380 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 20, right: 16, bottom: 70, left: 0 }}>
            <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" />
            <XAxis dataKey="position" tick={{ fontSize: 11, fill: "#64748B" }} angle={-22} textAnchor="end" interval={0} height={70} />
            <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} label={{ value: "Number of Applications", angle: -90, position: "insideLeft", fontSize: 12, fill: "#64748B" }} />
            <Tooltip />
            <Bar dataKey="applications" radius={[2, 2, 0, 0]}>
              {data.map((_d, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
